import { useState } from "react";
import { useEffect } from "react";

import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import { Box, Grid, Tooltip, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { v4 as uuid } from "uuid";

import OmniAssessmentValueTextFab from "../ui/omniAssessmentValueTextFab";
import OmniTextField from "../ui/omniTextField";

const getAnswerTextAsHTML = text => {
  return { __html: `${text}` };
};

const QuestionHeightDisplay = props => {
  const { question, onAnswerSelected, startNextTimer } = props;

  const theme = useTheme();

  const defaultDelay = 350;

  const [validAnswerSelected, setValidAnswerSelected] = useState(question.answerValue != null);

  const [heightFeet, setHeightFeet] = useState("");
  const [heightInches, setHeightInches] = useState("");

  const [unsure, setUnsure] = useState(false);
  const [errorHeightFeet, setErrorHeightFeet] = useState(false);
  const [errorHeightInches, setErrorHeightInches] = useState(false);

  useEffect(() => {
    if (question.answerValue == null) {
      setUnsure(false);
    } else if (question.answerValue && question.answerValue[0] === "unsure") {
      setUnsure(true);
      setErrorHeightFeet(false);
      setErrorHeightInches(false);
    } else {
      setUnsure(false);
      setHeightFeet(parseInt(question.answerValue[0] / 12));
      setHeightInches(parseInt(question.answerValue[0] % 12));
    }
  }, [question]);

  useEffect(() => {
    setErrorHeightFeet(heightFeet === "" || heightFeet < 2 || heightFeet > 9 ? true : false);
    setErrorHeightInches(heightInches === "" || heightInches > 11 ? true : false);

    if (heightInches || heightFeet) {
      setUnsure(false);
    }

    if (
      heightFeet === "" ||
      heightFeet < 2 ||
      heightFeet > 9 ||
      heightInches === "" ||
      heightInches > 11
    ) {
      onAnswerSelected(null);
      setValidAnswerSelected(false);
    } else {
      onAnswerSelected([parseInt(heightFeet) * 12 + parseInt(heightInches)]);
      setValidAnswerSelected(true);
    }
  }, [
    heightInches,
    heightFeet,
    setUnsure,
    setErrorHeightFeet,
    setErrorHeightInches,
    onAnswerSelected,
  ]);

  return (
    <Grid container item direction="row" spacing={2} justifyContent="center">
      <Grid item xs={12} padding={1}>
        <Box
          sx={{ justifyContent: "center", display: "flex", width: "100%", flexDirection: "row" }}
        >
          <OmniTextField
            sx={{ width: "110px", marginRight: theme.spacing(1) }}
            key={`answerOptionQuestion${question.omniID}HF`}
            name={uuid()}
            value={heightFeet}
            label={"Feet"}
            type="number"
            error={errorHeightFeet}
            onChange={event => {
              let onlyNums = event.target.value.toString();
              onlyNums = onlyNums.replace(/[^0-9]/g, "");
              onlyNums = onlyNums.slice(0, 1);
              setHeightFeet(onlyNums);
            }}
            onFocus={event => event.target.select()}
            onKeyPress={e => {
              if (e.key === "Enter" && validAnswerSelected) {
                startNextTimer(defaultDelay);
              }
            }}
            autoFocus
          />
          <OmniTextField
            sx={{ width: "120px", marginLeft: theme.spacing(1) }}
            key={`answerOptionQuestion${question.omniID}HI`}
            name={uuid()}
            type="number"
            value={heightInches}
            onFocus={event => event.target.select()}
            label={"Inches"}
            error={errorHeightInches}
            onChange={event => {
              let onlyNums = event.target.value.toString();
              onlyNums = onlyNums.replace(/[^0-9]/g, "");

              if (onlyNums !== "") {
                let theValue = parseInt(onlyNums.charAt(0));
                if (theValue > 1) {
                  onlyNums = onlyNums.slice(0, 1);
                } else {
                  onlyNums = onlyNums.slice(0, 2);
                }
              }

              setHeightInches(onlyNums);
            }}
            onKeyPress={e => {
              if (e.key === "Enter" && validAnswerSelected) {
                startNextTimer(defaultDelay);
              }
            }}
          />
        </Box>
      </Grid>

      <Grid item xs={11} sx={{ marginTop: theme.spacing(2) }}>
        <Grid
          id={`questionGrid1`}
          container
          item
          xs={11}
          justifyContent="center"
          spacing={2}
          sx={{ marginLeft: "4px" }}
        >
          <Tooltip title={question.answerOptions[0].helpText || ""} placement="left">
            <>
              <OmniAssessmentValueTextFab
                key={`answerOption${uuid()}`}
                color="primary"
                size="large"
                variant={"outlined"}
                onClick={e => {
                  e.stopPropagation();
                  setUnsure(true);
                  onAnswerSelected(["unsure"]);
                  startNextTimer(defaultDelay);
                }}
                sx={{ width: "100%", "&:hover": { color: theme.palette.primary.contrastText } }}
              >
                <Grid
                  container
                  justifyContent="center"
                  direction="row"
                  sx={{ width: "100%", alignItems: "center" }}
                >
                  <Grid item justifySelf="center" xs>
                    <Typography
                      variant="button"
                      dangerouslySetInnerHTML={getAnswerTextAsHTML("I don't know")}
                    />
                  </Grid>
                  {unsure && (
                    <CheckCircleRoundedIcon
                      sx={{
                        position: "absolute",
                        top: 8,
                        right: 16,
                        maxWidth: "32px",
                        maxHeight: "32px",
                        alignSelf: "baseline",
                      }}
                    />
                  )}
                </Grid>
              </OmniAssessmentValueTextFab>
            </>
          </Tooltip>
        </Grid>
      </Grid>
    </Grid>
  );
};

export default QuestionHeightDisplay;
